import { alpha, createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { selectUserSetting, StoredSettings } from './store/slices/settings';
  
// const fillPalette = {
//     friendly: '#61ccf0',
//     hostile: '#fd9792',
//     neutral: '#99cc67',
//     unknown: '#ffff80',

//     pending: '#ffff80',
// }


const BASE_COLORS = {
  white: '#ffffff',
  black: '#000000',
  grey: {
    '100': '#f7f7f9',
    '150': '#e8e8ec',
    '200': '#979aa0',
    '300': '#5f5f5f',
    '400': '#363636',
    '500': '#2d2d2d',
    '600': '#242424',
    '700': '#1d1d1d',
    '800': '#151515',

    primary: '#c7c7c7'
  },
  rag: {
    red: '#ff1b00',
    amber: '#ffbf00',
    green: '#00e56f'
  },
  affiliation: {
    friendly: '#80e0ff',
    hostile: '#ff8080',
    neutral: '#aaffaa',
    unknown: '#ffff80'
  },
  effector: '#dfff29'
};

const DARK_UI_COLORS = {
  mode: "dark",
  typography: {
    white: BASE_COLORS.grey['100'],
    light: BASE_COLORS.grey['200'],
    mid: BASE_COLORS.grey['300'],
    black: BASE_COLORS.grey['800'],
    green: BASE_COLORS.rag.green,
    amber: BASE_COLORS.rag.amber,
    red: BASE_COLORS.rag.red,
  },
  fill: {
    background: BASE_COLORS.black,
    base_dark01: BASE_COLORS.grey['800'],
    base_dark02: BASE_COLORS.grey['600'],
    base_min: BASE_COLORS.grey['300'],
    base_light02: BASE_COLORS.grey['200'],
    base_light01: BASE_COLORS.grey['100'],

    red: BASE_COLORS.rag.red,
    red_tint: alpha(BASE_COLORS.rag.red, 0.2),
    amber: BASE_COLORS.rag.amber,
    amber_tint: alpha(BASE_COLORS.rag.amber, 0.2),
    green: BASE_COLORS.rag.green,
    green_tint: alpha(BASE_COLORS.rag.green, 0.2),
    selected: BASE_COLORS.grey.primary,
    selected_tint: alpha(BASE_COLORS.grey.primary, 0.2),

    button_primary: BASE_COLORS.grey.primary,
    button_secondary: BASE_COLORS.grey['600'],
    button_engage: BASE_COLORS.rag.red,
    button_icononly: 'transparent',
    button_onimage: alpha(BASE_COLORS.grey['500'], 0.5),
    button_error: BASE_COLORS.rag.red,
    button_warning: BASE_COLORS.rag.amber,
    button_ok: BASE_COLORS.rag.green,
  },
  stroke: {
    dark: BASE_COLORS.black,
    default: BASE_COLORS.grey['500'],
    mid: BASE_COLORS.grey['300'],
    light: BASE_COLORS.grey['200'],
    white: BASE_COLORS.grey['100'],

    primary: BASE_COLORS.grey.primary,
    selected: BASE_COLORS.grey.primary,
    red: BASE_COLORS.rag.red,

    button_primary: BASE_COLORS.grey['500'],
    button_secondary: BASE_COLORS.grey['100'],
    button_engage: BASE_COLORS.black,
    button_icononly: BASE_COLORS.grey['100'],
    button_onimage: BASE_COLORS.grey['100'],
    button_error: BASE_COLORS.black,
    button_warning: BASE_COLORS.black,
    button_ok: BASE_COLORS.black,
  },
};


const LIGHT_UI_COLORS = {
  mode: "light",
  typography: {
    white: BASE_COLORS.grey['800'],
    light: BASE_COLORS.grey['700'],
    mid: BASE_COLORS.grey['600'],
    black: BASE_COLORS.grey['100'],
    green: BASE_COLORS.rag.green,
    red: BASE_COLORS.rag.red,
  },
  fill: {
    background: BASE_COLORS.white,
    base_dark01: BASE_COLORS.grey['100'],
    base_dark02: BASE_COLORS.grey['150'],
    base_min: BASE_COLORS.grey['600'],
    base_light02: BASE_COLORS.grey['700'],
    base_light01: BASE_COLORS.grey['800'],

    red: BASE_COLORS.rag.red,
    red_tint: alpha(BASE_COLORS.rag.red, 0.2),
    amber: BASE_COLORS.rag.amber,
    amber_tint: alpha(BASE_COLORS.rag.amber, 0.2),
    green: BASE_COLORS.rag.green,
    green_tint: alpha(BASE_COLORS.rag.green, 0.2),
    selected: BASE_COLORS.grey.primary,
    selected_tint: alpha(BASE_COLORS.grey.primary, 0.2),

    button_primary: BASE_COLORS.grey.primary,
    button_secondary: BASE_COLORS.grey['150'],
    button_engage: BASE_COLORS.rag.red,
    button_icononly: 'transparent',
    button_onimage: alpha(BASE_COLORS.grey['150'], 0.5),
    button_error: BASE_COLORS.rag.red,
    button_warning: BASE_COLORS.rag.amber,
    button_ok: BASE_COLORS.rag.green,
  },
  stroke: {
    dark: BASE_COLORS.white,
    default: BASE_COLORS.grey['150'],
    mid: BASE_COLORS.grey['600'],
    light: BASE_COLORS.grey['700'],
    white: BASE_COLORS.grey['800'],

    primary: BASE_COLORS.grey.primary,
    selected: BASE_COLORS.grey.primary,
    red: BASE_COLORS.rag.red,

    button_primary: BASE_COLORS.grey['400'],
    button_secondary: BASE_COLORS.grey['800'],
    button_engage: BASE_COLORS.white,
    button_icononly: BASE_COLORS.grey['800'],
    button_onimage: BASE_COLORS.grey['800'],
    button_error: BASE_COLORS.black,
    button_warning: BASE_COLORS.black,
    button_ok: BASE_COLORS.black,
  },
};

const darkMode = false;
const UI_COLORS = darkMode ? DARK_UI_COLORS : LIGHT_UI_COLORS;


// Default browser font-size is 16px
const ptToPixel = (pt) => `${pt / 16}em`;

const includeFallbackFonts = (font) => [font, 'sans-serif'].join(', ')

const baseTheme = createTheme();

const generateTheme = (UI_COLORS) => {

    const theme = createTheme({
      spacing: factor => `${0.25 * factor}rem`,
      breakpoints: baseTheme.breakpoints,
      palette: {
        mode: UI_COLORS.mode,
          primary: {
            main: UI_COLORS.typography.white,
            contrastText: UI_COLORS.typography.black,
          },
          secondary: {
            main: UI_COLORS.typography.light,
            contrastText: UI_COLORS.typography.black,
          },
          engage: {
            main: '#ff0000',
            contrastText: 'white',
          },
          friendly: {
            main: '#61ccf0',
            contrastText: '#000',
          },
          hostile: {
            main: '#fd9792',
            contrastText: '#000',
          },
          neutral: {
            main: '#99cc67',
            contrastText: '#000',
          },
          unknown: {
            main: '#ffff80',
            contrastText: '#000',
          },
          pending: {
            main: '#ffff80',
            contrastText: '#000',
          },
          unselected: {
            main: '#cdcdcd',
            contrastText: '#000',
          }
        },
        typography: {
          allVariants: {
            color: UI_COLORS.typography.white,
            display: 'flex',
            alignItems: 'center',
            lineHeight: 1,
          },
          title: {
            fontFamily: includeFallbackFonts('DM Mono'),
            fontSize: ptToPixel(64),
            fontWeight: 'normal',
            textTransform: 'uppercase',
          },

          h1: {
            fontFamily: includeFallbackFonts('DM Mono'),
            fontSize: ptToPixel(32),
            fontWeight: 'normal',
            textTransform: 'uppercase',
            // lineHeight: 1.25,
            
            // [baseTheme.breakpoints.down('xl')]: {
            //   fontSize: ptToPixel(24),
            // },
          },
          h2: {
            fontFamily: includeFallbackFonts('DM Sans'),
            fontSize: ptToPixel(24),
            // lineHeight: 4 / 3,
            letterSpacing: '2',
            // [baseTheme.breakpoints.down('xl')]: {
            //   fontSize: ptToPixel(20),
            // },
          },
          h3: { // This is not part of the Wondermake design and added by JRL
            fontFamily: includeFallbackFonts('DM Sans'),
            fontSize: ptToPixel(20),
            // lineHeight: 7 / 5,
            letterSpacing: '2',
            // [baseTheme.breakpoints.down('xl')]: {
            //   fontSize: ptToPixel(16),
            // },
          },
          body: {
            fontFamily: includeFallbackFonts('DM Sans'),
            fontSize: ptToPixel(16),
            // lineHeight: 5 / 4,

            // [baseTheme.breakpoints.down('xl')]: {
            //   fontSize: ptToPixel(14),
            // },
          },
          bodysmall: {
            fontFamily: includeFallbackFonts('DM Sans'),
            fontSize: ptToPixel(14),
            // lineHeight: 10 / 7,

            // [baseTheme.breakpoints.down('xl')]: {
            //   fontSize: ptToPixel(12),
            // },
          },
          overline: {
            fontFamily: includeFallbackFonts('DM Mono'),
            fontSize: ptToPixel(12),
            // lineHeight: 1,
            textTransform: 'uppercase',

            // [baseTheme.breakpoints.down('xl')]: {
            //   fontSize: ptToPixel(10),
            // },
          },
          overlinesmall: {
            fontFamily: includeFallbackFonts('DM Mono'),
            fontSize: ptToPixel(10),
            // lineHeight: 6 / 5,
            textTransform: 'uppercase',

            // [baseTheme.breakpoints.down('xl')]: {
            //   fontSize: ptToPixel(8),
            // },
          },

          // fontFamily: '"DM Mono", monospace',
          button: {
            fontFamily: includeFallbackFonts('DM Mono'),
            fontSize: ptToPixel(14),
            // lineHeight: ptToPixel(14),
            // letterSpacing: '',
            textTransform: 'uppercase',

            // [baseTheme.breakpoints.down('xl')]: {
            //   fontSize: ptToPixel(12),
            // },
          }
        },
        components: {
          MuiButton: {
            defaultProps: {
              variant: 'contained',
            },
          },

          MuiButtonBase: {
            defaultProps: {
                disableRipple: true,
            },
          },

          MuiSelect: {
            styleOverrides: {
              root: {
                backgroundColor: '#00000040',
                border: 'none',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              },
              select: {
                padding: '6px',
                paddingLeft: '8px',
              },
            },
          },

          MuiRadio: {
            defaultProps: {
              size: "small",
            },
          },

          MuiCheckbox: {
            defaultProps: {
              size: "small",
            },
          },

          MuiMenuItem: {
            styleOverrides: {
              root: {
                fontFamily: 'Space Grotesk, sans-serif',
                padding: '6px',
              },
            },
          }
        },
        shape: {
          borderRadius: 0,
        },
    });

    theme.palette.ui_colors = UI_COLORS;

    return theme;
}

const themes = {
    light: generateTheme(LIGHT_UI_COLORS),
    dark: generateTheme(DARK_UI_COLORS),
}

export function ThemeProvider ({children}) {
    const themeName = useSelector(selectUserSetting(StoredSettings.ui_theme));
    const theme = themes[themeName] ?? themes.light;
    
    return (
        <MuiThemeProvider theme={theme}>
            {children}
        </MuiThemeProvider>
    )
}

// export const withTheme = (InnerComponent) => {
//     return () => (
//         <MuiThemeProvider theme={theme}>
//             <InnerComponent />
//         </MuiThemeProvider>
//     )
// }

